import { StockData, CalculatedMetrics, KPIMetrics } from '../types';

export const calculateMetrics = (data: StockData[]): CalculatedMetrics[] => {
  const calculatedData = data.map(row => {
    // Funzione helper per calcolare percentuali e ratio in sicurezza
    const safePercentage = (numerator: number, denominator: number): number => {
      if (!denominator || denominator === 0 || !isFinite(numerator) || !isFinite(denominator)) {
        return 0;
      }
      return numerator / denominator;
    };

    const safeRatio = (numerator: number, denominator: number): number => {
      if (!denominator || denominator === 0 || !isFinite(numerator) || !isFinite(denominator)) {
        return 0;
      }
      return numerator / denominator;
    };

    // Assicura che tutti i valori di input siano numeri validi
    const open = Number(row.Open) || 0;
    const close = Number(row.Close) || 0;
    const high = Number(row.High) || 0;
    const volume = Number(row.Volume) || 0;
    const preMarketVolume = Number(row['Pre Market Volume']) || 0;
    const preMarketHigh = Number(row['Pre Market High']) || 0;
    const preMarketLow = Number(row['Pre Market Low']) || 0;
    const sharesOutstanding = Number(row['Shares Outstanding']) || 0;
    const oneMinHigh = Number(row['1 Min High']) || 0;
    const fiveMinLow = Number(row['5 Min Low']) || 0;
    const high30m = Number(row['High 30m']) || 0;
    const low30m = Number(row['Low 30m']) || 0;
    const volume30m = Number(row['Volume 30m']) || 0;

    // Calcola Gap % usando la formula corretta: ((Open - Pre Market Low) / Pre Market Low) * 100
    // Ma usa il valore già presente nel dataset se disponibile
    let gapPercent = Number(row['Gap %']) || 0;
    
    // Se il Gap % non è presente o è zero, calcolalo usando la formula
    if (gapPercent === 0 && preMarketLow > 0) {
      gapPercent = ((open - preMarketLow) / preMarketLow);
    }

    return {
      ...row,
      'Red Close': close < open ? 1 : 0,
      'Change %': safePercentage(close - open, open),
      'Spike %': safePercentage(high - open, open),
      '5 Min Low %': safePercentage(fiveMinLow - open, open),
      '1 Min High %': safePercentage(oneMinHigh - open, open),
      '30 Min High %': safePercentage(high30m - open, open),
      '30 Min Low %': safePercentage(low30m - open, open),
      'HIGH > PMH': high > preMarketHigh ? 'Y' : 'N',
      '% PMH to HIGH': safePercentage(high - preMarketHigh, preMarketHigh),
      'Open to PMH': safePercentage(open - preMarketHigh, preMarketHigh),
      'Vol/PM Vol Ratio': safeRatio(volume, preMarketVolume),
      'OS Rotation': safeRatio(volume, sharesOutstanding),
      'Vol 10-11/PM Vol': safeRatio(Number(row['Volume 10-11']) || 0, preMarketVolume),
      'Vol 11-12/PM Vol': safeRatio(Number(row['Volume 11-12']) || 0, preMarketVolume),
      'Vol 12-13/PM Vol': safeRatio(Number(row['Volume 12-13']) || 0, preMarketVolume),
      'Vol 13-14/PM Vol': safeRatio(Number(row['Volume 13-14']) || 0, preMarketVolume),
      'Vol 14-15/PM Vol': safeRatio(Number(row['Volume 14-15']) || 0, preMarketVolume),
      'Vol 15-16/PM Vol': safeRatio(Number(row['Volume 15-16']) || 0, preMarketVolume),
      'Vol 30m/PM Vol': safeRatio(volume30m, preMarketVolume),
      // Assicura che Gap % sia correttamente assegnato
      'Gap %': gapPercent,
    };
  });

  // RIMOSSO IL FILTRO OBBLIGATORIO: Ora restituisce TUTTI i dati calcolati
  // L'utente può applicare i filtri che desidera tramite l'interfaccia utente
  console.log(`Dati calcolati: ${calculatedData.length} record totali (nessun filtro automatico applicato)`);
  
  return calculatedData;
};

export interface AggregatedTickerMetrics {
  ticker: string;
  totalOccurrences: number;
  averageGapPercent: number;
  medianGapPercent: number;
  averageChangePercent: number;
  medianChangePercent: number;
  averageSpikePercent: number;
  medianSpikePercent: number;
  averageOpenPrice: number;
  medianOpenPrice: number;
  averageHighPrice: number;
  medianHighPrice: number;
  averageLowPrice: number;
  medianLowPrice: number;
  averageClosePrice: number;
  medianClosePrice: number;
  averageMarketCap: number;
  medianMarketCap: number;
  averageSharesOutstanding: number;
  medianSharesOutstanding: number;
  averagePmVolume: number;
  medianPmVolume: number;
  averageEodVolume: number;
  medianEodVolume: number;
  averageVolumeRatio: number;
  medianVolumeRatio: number;
  redClosePercentage: number;
  pmhBreakPercentage: number;
  averagePmHigh: number;
  medianPmHigh: number;
  averagePmLow: number;
  medianPmLow: number;
  averagePmhToHighPercent: number;
  medianPmhToHighPercent: number;
  averageOsRotation: number;
  medianOsRotation: number;
  average1MinHighPercent: number;
  median1MinHighPercent: number;
  average5MinLowPercent: number;
  median5MinLowPercent: number;
  average30MinHighPercent: number;
  median30MinHighPercent: number;
  average30MinLowPercent: number;
  median30MinLowPercent: number;
  hodAverageTime: string;
  lodAverageTime: string;
  allOccurrences: CalculatedMetrics[];
}

// Funzione helper per calcolare la mediana
export const calculateMedian = (values: number[]): number => {
  const validValues = values.filter(v => isFinite(v) && !isNaN(v));
  if (validValues.length === 0) return 0;
  
  const sorted = validValues.sort((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  
  if (sorted.length % 2 === 0) {
    return (sorted[middle - 1] + sorted[middle]) / 2;
  } else {
    return sorted[middle];
  }
};

// Funzione helper per calcolare la deviazione standard
export const calculateStandardDeviation = (values: number[]): number => {
  const validValues = values.filter(v => isFinite(v) && !isNaN(v));
  if (validValues.length === 0) return 0;
  
  const mean = validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
  const squaredDiffs = validValues.map(value => Math.pow(value - mean, 2));
  const variance = squaredDiffs.reduce((sum, val) => sum + val, 0) / validValues.length;
  
  return Math.sqrt(variance);
};

// Funzione helper per calcolare l'orario medio
export const averageTime = (times: (Date | string | null | undefined)[]): string => {
  const validTimes = times.filter(time => time !== null && time !== undefined);
  if (validTimes.length === 0) return '00:00';
  
  const totalMinutes = validTimes.reduce((sum, time) => {
    let minutes = 0;
    
    if (time instanceof Date) {
      minutes = time.getHours() * 60 + time.getMinutes();
    } else if (typeof time === 'string' && time.includes(':')) {
      const [hours, mins] = time.split(':').map(Number);
      minutes = (hours || 0) * 60 + (mins || 0);
    }
    
    return sum + minutes;
  }, 0);
  
  const avgMinutes = Math.round(totalMinutes / validTimes.length);
  const hours = Math.floor(avgMinutes / 60);
  const mins = avgMinutes % 60;
  
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

// Funzioni helper per parsing e formattazione del tempo
const parseTime = (timeValue: Date | string | number | null | undefined): number => {
  if (timeValue instanceof Date) {
    return timeValue.getHours() * 60 + timeValue.getMinutes();
  }
  if (typeof timeValue === 'string' && timeValue.includes(':')) {
    const [hours, minutes] = timeValue.split(':').map(Number);
    return (hours || 0) * 60 + (minutes || 0);
  }
  return NaN;
};

const formatTime = (minutes: number): string => {
  if (!isFinite(minutes) || isNaN(minutes)) return '00:00';
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
};

export const calculateAggregatedTickerMetrics = (tickerData: CalculatedMetrics[]): AggregatedTickerMetrics => {
  if (tickerData.length === 0) {
    throw new Error('Nessun dato fornito per l\'aggregazione ticker');
  }

  const ticker = tickerData[0].Ticker;
  const totalOccurrences = tickerData.length;

  // Funzione helper per calcolare medie in sicurezza
  const safeAverage = (values: number[]): number => {
    const validValues = values.filter(v => isFinite(v) && !isNaN(v));
    if (validValues.length === 0) return 0;
    return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
  };

  // Calcola medie e mediane per valori numerici
  const gapPercentValues = tickerData.map(row => Number(row['Gap %']) || 0);
  const changePercentValues = tickerData.map(row => Number(row['Change %']) || 0);
  const spikePercentValues = tickerData.map(row => Number(row['Spike %']) || 0);
  const openPriceValues = tickerData.map(row => Number(row.Open) || 0);
  const highPriceValues = tickerData.map(row => Number(row.High) || 0);
  const lowPriceValues = tickerData.map(row => Number(row.Low) || 0);
  const closePriceValues = tickerData.map(row => Number(row.Close) || 0);
  const marketCapValues = tickerData.map(row => Number(row['Market Cap']) || 0);
  const sharesOutstandingValues = tickerData.map(row => Number(row['Shares Outstanding']) || 0);
  const pmVolumeValues = tickerData.map(row => Number(row['Pre Market Volume']) || 0);
  const eodVolumeValues = tickerData.map(row => Number(row.Volume) || 0);
  const volumeRatioValues = tickerData.map(row => Number(row['Vol/PM Vol Ratio']) || 0);
  const pmHighValues = tickerData.map(row => Number(row['Pre Market High']) || 0);
  const pmLowValues = tickerData.map(row => Number(row['Pre Market Low']) || 0);
  const pmhToHighPercentValues = tickerData.map(row => Number(row['% PMH to HIGH']) || 0);
  const osRotationValues = tickerData.map(row => Number(row['OS Rotation']) || 0);
  const oneMinHighPercentValues = tickerData.map(row => Number(row['1 Min High %']) || 0);
  const fiveMinLowPercentValues = tickerData.map(row => Number(row['5 Min Low %']) || 0);
  const thirtyMinHighPercentValues = tickerData.map(row => Number(row['30 Min High %']) || 0);
  const thirtyMinLowPercentValues = tickerData.map(row => Number(row['30 Min Low %']) || 0);

  // Calcola tempi medi HOD e LOD
  const hodTimes = tickerData.map(row => parseTime(row['Hod Time'])).filter(time => !isNaN(time));
  const lodTimes = tickerData.map(row => parseTime(row['Lod Time'])).filter(time => !isNaN(time));

  const avgHodTime = hodTimes.length > 0 ? hodTimes.reduce((sum, time) => sum + time, 0) / hodTimes.length : 0;
  const avgLodTime = lodTimes.length > 0 ? lodTimes.reduce((sum, time) => sum + time, 0) / lodTimes.length : 0;

  // Calcola percentuali per valori booleani
  const redCloses = tickerData.filter(row => row['Red Close'] === 1).length;
  const pmhBreaks = tickerData.filter(row => row['HIGH > PMH'] === 'Y').length;
  
  const redClosePercentage = (redCloses / totalOccurrences) * 100;
  const pmhBreakPercentage = (pmhBreaks / totalOccurrences) * 100;

  return {
    ticker,
    totalOccurrences,
    averageGapPercent: safeAverage(gapPercentValues),
    medianGapPercent: calculateMedian(gapPercentValues),
    averageChangePercent: safeAverage(changePercentValues),
    medianChangePercent: calculateMedian(changePercentValues),
    averageSpikePercent: safeAverage(spikePercentValues),
    medianSpikePercent: calculateMedian(spikePercentValues),
    averageOpenPrice: safeAverage(openPriceValues),
    medianOpenPrice: calculateMedian(openPriceValues),
    averageHighPrice: safeAverage(highPriceValues),
    medianHighPrice: calculateMedian(highPriceValues),
    averageLowPrice: safeAverage(lowPriceValues),
    medianLowPrice: calculateMedian(lowPriceValues),
    averageClosePrice: safeAverage(closePriceValues),
    medianClosePrice: calculateMedian(closePriceValues),
    averageMarketCap: safeAverage(marketCapValues),
    medianMarketCap: calculateMedian(marketCapValues),
    averageSharesOutstanding: safeAverage(sharesOutstandingValues),
    medianSharesOutstanding: calculateMedian(sharesOutstandingValues),
    averagePmVolume: safeAverage(pmVolumeValues),
    medianPmVolume: calculateMedian(pmVolumeValues),
    averageEodVolume: safeAverage(eodVolumeValues),
    medianEodVolume: calculateMedian(eodVolumeValues),
    averageVolumeRatio: safeAverage(volumeRatioValues),
    medianVolumeRatio: calculateMedian(volumeRatioValues),
    redClosePercentage,
    pmhBreakPercentage,
    averagePmHigh: safeAverage(pmHighValues),
    medianPmHigh: calculateMedian(pmHighValues),
    averagePmLow: safeAverage(pmLowValues),
    medianPmLow: calculateMedian(pmLowValues),
    averagePmhToHighPercent: safeAverage(pmhToHighPercentValues),
    medianPmhToHighPercent: calculateMedian(pmhToHighPercentValues),
    averageOsRotation: safeAverage(osRotationValues),
    medianOsRotation: calculateMedian(osRotationValues),
    average1MinHighPercent: safeAverage(oneMinHighPercentValues),
    median1MinHighPercent: calculateMedian(oneMinHighPercentValues),
    average5MinLowPercent: safeAverage(fiveMinLowPercentValues),
    median5MinLowPercent: calculateMedian(fiveMinLowPercentValues),
    average30MinHighPercent: safeAverage(thirtyMinHighPercentValues),
    median30MinHighPercent: calculateMedian(thirtyMinHighPercentValues),
    average30MinLowPercent: safeAverage(thirtyMinLowPercentValues),
    median30MinLowPercent: calculateMedian(thirtyMinLowPercentValues),
    hodAverageTime: formatTime(Math.round(avgHodTime)),
    lodAverageTime: formatTime(Math.round(avgLodTime)),
    allOccurrences: tickerData
  };
};

export const calculateKPIs = (data: CalculatedMetrics[]): KPIMetrics => {
  if (data.length === 0) {
    return {
      numberOfGaps: 0,
      averageMarketCap: 0,
      medianMarketCap: 0,
      averageSharesOutstanding: 0,
      medianSharesOutstanding: 0,
      pmEodVolumeRatio: 0,
      medianPmEodVolumeRatio: 0,
      averagePmVolume: 0,
      medianPmVolume: 0,
      averageEodVolume: 0,
      medianEodVolume: 0,
      closeRedPercentage: 0,
      pmhBreakPercentage: 0,
      hodAverageTime: '00:00',
      lodAverageTime: '00:00',
      averageSpikePercent: 0,
      medianSpikePercent: 0,
      average30MinHighPercent: 0,
      median30MinHighPercent: 0,
      average30MinLowPercent: 0,
      median30MinLowPercent: 0,
      averageOpenToPmh: 0,
      medianOpenToPmh: 0,
      averagePmhToHigh: 0,
      medianPmhToHigh: 0,
      average30MinVolPmVolRatio: 0,
      median30MinVolPmVolRatio: 0,
      averagePositiveChangePercent: 0,
      averageNegativeChangePercent: 0,
      averageOsRotation: 0, // Aggiunto per supportare il nuovo insight
    };
  }

  // Funzione helper per calcolare medie in sicurezza
  const safeAverage = (values: number[]): number => {
    const validValues = values.filter(v => isFinite(v) && !isNaN(v));
    if (validValues.length === 0) return 0;
    return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
  };

  const numberOfGaps = data.length;
  
  // Estrai valori per i calcoli
  const marketCapValues = data.map(row => Number(row['Market Cap']) || 0);
  const sharesOutstandingValues = data.map(row => Number(row['Shares Outstanding']) || 0);
  const pmVolumeValues = data.map(row => Number(row['Pre Market Volume']) || 0);
  const eodVolumeValues = data.map(row => Number(row.Volume) || 0);
  const spikePercentValues = data.map(row => Number(row['Spike %']) || 0);
  const thirtyMinHighPercentValues = data.map(row => Number(row['30 Min High %']) || 0);
  const thirtyMinLowPercentValues = data.map(row => Number(row['30 Min Low %']) || 0);
  const openToPmhValues = data.map(row => Number(row['Open to PMH']) || 0);
  const pmhToHighValues = data.map(row => Number(row['% PMH to HIGH']) || 0);
  const thirtyMinVolPmVolRatioValues = data.map(row => Number(row['Vol 30m/PM Vol']) || 0);
  const osRotationValues = data.map(row => Number(row['OS Rotation']) || 0); // Aggiunto per il nuovo insight

  // Separa chiusure positive e negative per calcolare le medie di Change %
  const positiveCloses = data.filter(row => row['Red Close'] === 0); // Chiusure verdi
  const negativeCloses = data.filter(row => row['Red Close'] === 1); // Chiusure rosse

  const positiveChangeValues = positiveCloses.map(row => Number(row['Change %']) || 0);
  const negativeChangeValues = negativeCloses.map(row => Number(row['Change %']) || 0);

  // Calcola ratio volume
  const volumeRatios = data.map(row => {
    const pmVol = Number(row['Pre Market Volume']) || 0;
    const eodVol = Number(row.Volume) || 0;
    return pmVol > 0 ? eodVol / pmVol : 0;
  });

  const redCloses = data.filter(row => row['Red Close'] === 1).length;
  const pmhBreaks = data.filter(row => row['HIGH > PMH'] === 'Y').length;

  const hodTimes = data.map(row => parseTime(row['Hod Time'])).filter(time => !isNaN(time));
  const lodTimes = data.map(row => parseTime(row['Lod Time'])).filter(time => !isNaN(time));

  const avgHodTime = hodTimes.length > 0 ? hodTimes.reduce((sum, time) => sum + time, 0) / hodTimes.length : 0;
  const avgLodTime = lodTimes.length > 0 ? lodTimes.reduce((sum, time) => sum + time, 0) / lodTimes.length : 0;

  return {
    numberOfGaps,
    averageMarketCap: safeAverage(marketCapValues),
    medianMarketCap: calculateMedian(marketCapValues),
    averageSharesOutstanding: safeAverage(sharesOutstandingValues),
    medianSharesOutstanding: calculateMedian(sharesOutstandingValues),
    pmEodVolumeRatio: safeAverage(volumeRatios),
    medianPmEodVolumeRatio: calculateMedian(volumeRatios),
    averagePmVolume: safeAverage(pmVolumeValues),
    medianPmVolume: calculateMedian(pmVolumeValues),
    averageEodVolume: safeAverage(eodVolumeValues),
    medianEodVolume: calculateMedian(eodVolumeValues),
    closeRedPercentage: (redCloses / numberOfGaps) * 100,
    pmhBreakPercentage: (pmhBreaks / numberOfGaps) * 100,
    hodAverageTime: formatTime(Math.round(avgHodTime)),
    lodAverageTime: formatTime(Math.round(avgLodTime)),
    averageSpikePercent: safeAverage(spikePercentValues),
    medianSpikePercent: calculateMedian(spikePercentValues),
    average30MinHighPercent: safeAverage(thirtyMinHighPercentValues),
    median30MinHighPercent: calculateMedian(thirtyMinHighPercentValues),
    average30MinLowPercent: safeAverage(thirtyMinLowPercentValues),
    median30MinLowPercent: calculateMedian(thirtyMinLowPercentValues),
    averageOpenToPmh: safeAverage(openToPmhValues),
    medianOpenToPmh: calculateMedian(openToPmhValues),
    averagePmhToHigh: safeAverage(pmhToHighValues),
    medianPmhToHigh: calculateMedian(pmhToHighValues),
    average30MinVolPmVolRatio: safeAverage(thirtyMinVolPmVolRatioValues),
    median30MinVolPmVolRatio: calculateMedian(thirtyMinVolPmVolRatioValues),
    averagePositiveChangePercent: safeAverage(positiveChangeValues),
    averageNegativeChangePercent: safeAverage(negativeChangeValues),
    averageOsRotation: safeAverage(osRotationValues), // Aggiunto per il nuovo insight
  };
};

export const formatVolume = (volume: number): string => {
  if (!isFinite(volume) || isNaN(volume)) return '0';
  
  if (volume >= 1000000000) {
    return `${(volume / 1000000000).toFixed(1)}B`;
  } else if (volume >= 1000000) {
    return `${(volume / 1000000).toFixed(1)}M`;
  } else if (volume >= 1000) {
    return `${(volume / 1000).toFixed(1)}K`;
  }
  return volume.toString();
};

export const formatMarketCap = (marketCap: number): string => {
  if (!isFinite(marketCap) || isNaN(marketCap)) return '$0';
  
  if (marketCap >= 1000000000) {
    return `$${(marketCap / 1000000000).toFixed(1)}B`;
  } else if (marketCap >= 1000000) {
    return `$${(marketCap / 1000000).toFixed(1)}M`;
  } else if (marketCap >= 1000) {
    return `$${(marketCap / 1000).toFixed(1)}K`;
  }
  return `$${marketCap.toFixed(0)}`;
};

export const formatPercentage = (value: number): string => {
  if (!isFinite(value) || isNaN(value)) return '0.00%';
  
  // Il valore è già un decimale (es. 0.32 per 32%), quindi moltiplica per 100
  const percentage = value * 100;
  return `${percentage >= 0 ? '+' : ''}${percentage.toFixed(2)}%`;
};

// Esporta la funzione safeAverage per riutilizzo in altri componenti
export const safeAverage = (values: number[]): number => {
  const validValues = values.filter(v => isFinite(v) && !isNaN(v));
  if (validValues.length === 0) return 0;
  return validValues.reduce((sum, val) => sum + val, 0) / validValues.length;
};